/**
 * Business Hours Store - Multi-location
 * Alpine.js store para el estado de apertura del negocio
 * Soporta multiples ubicaciones con diferentes horarios
 */

// Dias de cierre especial (festivos, vacaciones, etc.)
// Formato: { month: 1-12, day: 1-31, message: 'Mensaje opcional' }
const CLOSED_DATES = [
  { month: 12, day: 24, message: 'Cerrado por Nochebuena' },
  { month: 12, day: 31, message: 'Cerrado por Nochevieja' },
];

// Configuracion de ubicaciones y horarios
const LOCATIONS = {
  alguazas: {
    name: 'Molina / Alguazas / Archena',
    schedules: [
      // Mie-Dom: 19:30 - 23:00
      { days: [3, 4, 5, 6, 0], open: { hour: 19, minute: 30 }, close: { hour: 23, minute: 0 } }
    ]
  },
  murcia: {
    name: 'Murcia ciudad',
    schedules: [
      // Mie-Jue: 20:00 - 23:00
      { days: [3, 4], open: { hour: 20, minute: 0 }, close: { hour: 23, minute: 0 } },
      // Vie-Sab-Dom: 13:00 - 15:30 (mediodia)
      { days: [5, 6, 0], open: { hour: 13, minute: 0 }, close: { hour: 15, minute: 30 } },
      // Vie-Sab-Dom: 20:00 - 23:00 (noche)
      { days: [5, 6, 0], open: { hour: 20, minute: 0 }, close: { hour: 23, minute: 0 } }
    ]
  }
};

export default function businessHoursStore() {
  return {
    // Estados
    isOpen: false,
    canOrder: false,
    isPreOrder: false,
    closedMessage: 'Cerrado',
    nextOpeningMessage: '',
    preOrderMessage: '',
    updateInterval: null,

    // Estado por ubicacion
    locationStatus: {
      alguazas: { isOpen: false, isPreOrder: false },
      murcia: { isOpen: false, isPreOrder: false }
    },

    // Configuracion general
    config: {
      timezone: 'Europe/Madrid',
      preOrderMinutes: 120, // 2 horas antes - permite reservas
    },

    init() {
      this.updateStatus();
      // Actualizar cada minuto
      this.updateInterval = setInterval(() => {
        this.updateStatus();
      }, 60000);
    },

    /**
     * Obtener hora actual en zona horaria de España
     */
    getCurrentTime() {
      return new Date(
        new Date().toLocaleString('en-US', {
          timeZone: this.config.timezone,
        })
      );
    },

    /**
     * Verificar si hoy es un dia de cierre especial (festivo, vacaciones, etc.)
     * @returns {Object|null} - Objeto con info del cierre o null si no es dia especial
     */
    checkSpecialClosedDate() {
      const now = this.getCurrentTime();
      const currentMonth = now.getMonth() + 1; // getMonth() es 0-indexed
      const currentDay = now.getDate();

      for (const closedDate of CLOSED_DATES) {
        if (closedDate.month === currentMonth && closedDate.day === currentDay) {
          return closedDate;
        }
      }
      return null;
    },

    /**
     * Verificar si una fecha especifica es un dia de cierre especial
     * @param {number} month - Mes (1-12)
     * @param {number} day - Dia del mes (1-31)
     * @returns {boolean}
     */
    isSpecialClosedDay(month, day) {
      for (const closedDate of CLOSED_DATES) {
        if (closedDate.month === month && closedDate.day === day) {
          return true;
        }
      }
      return false;
    },

    /**
     * Verificar si una ubicacion esta abierta
     */
    checkLocationOpen(locationKey) {
      const location = LOCATIONS[locationKey];
      if (!location) return false;

      const now = this.getCurrentTime();
      const currentDay = now.getDay();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const currentTimeInMinutes = currentHour * 60 + currentMinute;

      for (const schedule of location.schedules) {
        if (schedule.days.includes(currentDay)) {
          const openMinutes = schedule.open.hour * 60 + schedule.open.minute;
          const closeMinutes = schedule.close.hour * 60 + schedule.close.minute;

          if (currentTimeInMinutes >= openMinutes && currentTimeInMinutes < closeMinutes) {
            return true;
          }
        }
      }
      return false;
    },

    /**
     * Verificar si una ubicacion esta en periodo de pre-pedido
     */
    checkLocationPreOrder(locationKey) {
      const location = LOCATIONS[locationKey];
      if (!location) return false;

      const now = this.getCurrentTime();
      const currentDay = now.getDay();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const currentTimeInMinutes = currentHour * 60 + currentMinute;

      for (const schedule of location.schedules) {
        if (schedule.days.includes(currentDay)) {
          const openMinutes = schedule.open.hour * 60 + schedule.open.minute;
          const preOrderStart = openMinutes - this.config.preOrderMinutes;

          if (currentTimeInMinutes >= preOrderStart && currentTimeInMinutes < openMinutes) {
            return true;
          }
        }
      }
      return false;
    },

    /**
     * Obtener el proximo horario de apertura de cualquier ubicacion
     * Ignora los dias de cierre especial (festivos, vacaciones, etc.)
     */
    getNextOpening() {
      const now = this.getCurrentTime();
      const currentDay = now.getDay();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const currentTimeInMinutes = currentHour * 60 + currentMinute;

      // Verificar si HOY es un dia de cierre especial
      const todayIsClosed = this.checkSpecialClosedDate() !== null;

      let nearestOpening = null;
      let nearestLocationName = '';

      for (const [key, location] of Object.entries(LOCATIONS)) {
        for (const schedule of location.schedules) {
          // Verificar si abre hoy mas tarde (SOLO si hoy NO es dia de cierre especial)
          if (!todayIsClosed && schedule.days.includes(currentDay)) {
            const openMinutes = schedule.open.hour * 60 + schedule.open.minute;
            if (openMinutes > currentTimeInMinutes) {
              const timeUntil = openMinutes - currentTimeInMinutes;
              if (!nearestOpening || timeUntil < nearestOpening.timeUntil) {
                nearestOpening = {
                  timeUntil,
                  hour: schedule.open.hour,
                  minute: schedule.open.minute,
                  isToday: true,
                  dayName: 'hoy'
                };
                nearestLocationName = location.name;
              }
            }
          }

          // Verificar proximos dias
          for (let daysAhead = 1; daysAhead <= 7; daysAhead++) {
            const futureDay = (currentDay + daysAhead) % 7;
            
            // Calcular la fecha futura para verificar si es dia de cierre especial
            const futureDate = new Date(now);
            futureDate.setDate(futureDate.getDate() + daysAhead);
            const futureMonth = futureDate.getMonth() + 1;
            const futureDayOfMonth = futureDate.getDate();
            
            // Saltar si es un dia de cierre especial
            if (this.isSpecialClosedDay(futureMonth, futureDayOfMonth)) {
              continue;
            }
            
            if (schedule.days.includes(futureDay)) {
              const openMinutes = schedule.open.hour * 60 + schedule.open.minute;
              const totalMinutesUntil = (daysAhead * 24 * 60) + openMinutes - currentTimeInMinutes;

              if (!nearestOpening || totalMinutesUntil < nearestOpening.timeUntil) {
                const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado'];
                nearestOpening = {
                  timeUntil: totalMinutesUntil,
                  hour: schedule.open.hour,
                  minute: schedule.open.minute,
                  isToday: false,
                  dayName: daysAhead === 1 ? 'mañana' : dayNames[futureDay]
                };
                nearestLocationName = location.name;
              }
              break; // Solo necesitamos el primer horario de ese dia
            }
          }
        }
      }

      return { opening: nearestOpening, locationName: nearestLocationName };
    },

    /**
     * Actualizar estado global
     */
    updateStatus() {
      // TESTING: Permitir pedidos siempre (activar para testing)
      const TESTING_MODE = false;
      if (TESTING_MODE) {
        this.isOpen = true;
        this.isPreOrder = false;
        this.canOrder = true;
        this.closedMessage = 'Abierto (TESTING)';
        return;
      }

      // Verificar si es un dia de cierre especial (festivos, vacaciones, etc.)
      const specialClosed = this.checkSpecialClosedDate();
      if (specialClosed) {
        this.isOpen = false;
        this.isPreOrder = false;
        this.canOrder = false;
        this.closedMessage = specialClosed.message || 'Cerrado';
        this.locationStatus.alguazas.isOpen = false;
        this.locationStatus.alguazas.isPreOrder = false;
        this.locationStatus.murcia.isOpen = false;
        this.locationStatus.murcia.isPreOrder = false;
        
        // Buscar proxima apertura (dia siguiente al festivo)
        const { opening } = this.getNextOpening();
        if (opening) {
          const timeFormatted = `${opening.hour}:${String(opening.minute).padStart(2, '0')}h`;
          if (opening.dayName === 'mañana') {
            this.nextOpeningMessage = `Abrimos mañana a las ${timeFormatted}`;
          } else {
            this.nextOpeningMessage = `Abrimos el ${opening.dayName} a las ${timeFormatted}`;
          }
        } else {
          this.nextOpeningMessage = 'Consulta nuestro horario';
        }
        return;
      }

      // Actualizar estado de cada ubicacion
      this.locationStatus.alguazas.isOpen = this.checkLocationOpen('alguazas');
      this.locationStatus.alguazas.isPreOrder = this.checkLocationPreOrder('alguazas');
      this.locationStatus.murcia.isOpen = this.checkLocationOpen('murcia');
      this.locationStatus.murcia.isPreOrder = this.checkLocationPreOrder('murcia');

      // Estado global: abierto si AL MENOS UNA ubicacion esta abierta
      const anyOpen = this.locationStatus.alguazas.isOpen || this.locationStatus.murcia.isOpen;
      const anyPreOrder = this.locationStatus.alguazas.isPreOrder || this.locationStatus.murcia.isPreOrder;

      this.isOpen = anyOpen;
      this.isPreOrder = !anyOpen && anyPreOrder;
      // Permitir pedidos si al menos una ubicacion esta abierta o en pre-pedido
      this.canOrder = anyOpen || anyPreOrder;

      // Generar mensajes
      if (this.isOpen) {
        // Al menos uno abierto
        const openLocations = [];
        if (this.locationStatus.murcia.isOpen) openLocations.push('Murcia');
        if (this.locationStatus.alguazas.isOpen) openLocations.push('Molina/Alguazas');

        if (openLocations.length === 2) {
          this.closedMessage = 'Abierto';
        } else {
          this.closedMessage = `Abierto en ${openLocations[0]}`;
        }
      } else if (this.isPreOrder) {
        // Reserva disponible (2 horas antes de abrir)
        const { opening } = this.getNextOpening();
        if (opening) {
          const timeFormatted = `${opening.hour}:${String(opening.minute).padStart(2, '0')}h`;
          this.closedMessage = 'Reserva disponible';
          this.preOrderMessage = `Haz tu reserva - Se preparara a las ${timeFormatted}`;
          this.nextOpeningMessage = `Tu pedido se preparara a las ${timeFormatted}`;
        } else {
          this.closedMessage = 'Reserva disponible';
          this.preOrderMessage = 'Haz tu reserva - Se preparara en la apertura';
        }
      } else {
        // Ambos cerrados
        this.closedMessage = 'Cerrado';

        const { opening } = this.getNextOpening();
        if (opening) {
          const timeFormatted = `${opening.hour}:${String(opening.minute).padStart(2, '0')}h`;
          if (opening.dayName === 'hoy') {
            this.nextOpeningMessage = `Abrimos hoy a las ${timeFormatted}`;
          } else if (opening.dayName === 'mañana') {
            this.nextOpeningMessage = `Abrimos mañana a las ${timeFormatted}`;
          } else {
            this.nextOpeningMessage = `Abrimos el ${opening.dayName} a las ${timeFormatted}`;
          }
        } else {
          this.nextOpeningMessage = 'Consulta nuestro horario';
        }
      }
    },

    /**
     * Cleanup
     */
    destroy() {
      if (this.updateInterval) {
        clearInterval(this.updateInterval);
        this.updateInterval = null;
      }
    },
  };
}
